import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  buildScheduleEventCommunicationTagPresentation,
  type ScheduleEventCommunicationPresentationInput,
} from "../../../../../_shared/_logic/communication-presentation"
import { type WorkOrderViewStatus } from "../../../../../_shared/_logic/outreach-presentation"

type AnnouncementSignalColor = "emerald" | "orange" | "blue" | "cyan" | "violet" | "rose" | "amber"

export type WorkOrderAnnouncementSignal = {
  id: string
  label: string
  subLabel?: string | null
  icon: string
  color: AnnouncementSignalColor
  tooltip?: string | null
}

export type WorkOrderAnnouncementEvidence = {
  hasDigitalAnnouncementPath: boolean
  hasOpenDigitalStep: boolean
  hasVerifiedDigitalStep: boolean
  verifiedDigitalLabel: string | null
  hasOpenLetterStep: boolean
  hasCompletedLetterStep: boolean
  completedLetterLabel: string | null
  guaranteedManualLetterAction: boolean
}

export type WorkOrderAnnouncementOverview = {
  state:
    | "secured"
    | "running"
    | "verified_contact"
    | "digital_and_letter"
    | "letter_required"
    | "unverified_only"
    | "unsecured"
    | "no_plan"
    | "failed"
    | "completed"
  label: string
  subLabel: string | null
  definition: StatusDefinition<string>
  tooltip: string
  signals: WorkOrderAnnouncementSignal[]
  evidence: WorkOrderAnnouncementEvidence
  searchText: string
}

export const buildAnnouncementOverview = ({
  steps = [],
  appointmentStatus,
  communicationPresentation,
}: {
  contactOrder?: ContactOrder | null
  steps?: ContactOrderStep[] | null
  statusCode?: WorkOrderViewStatus | null
  appointmentStatus?: string | null
  communicationPresentation?: ScheduleEventCommunicationPresentationInput | null
  notificationOpenCount?: number
  notificationTitles?: string[]
}): WorkOrderAnnouncementOverview => {
  const presentation = buildScheduleEventCommunicationTagPresentation({
    status: appointmentStatus ?? "RESERVED",
    presentation: communicationPresentation,
  })
  const terminal = new Set([
    "COMPLETED",
    "DONE",
    "CANCELED",
    "CANCELLED",
    "FAILED",
    "ERROR",
    "SKIPPED",
  ])
  const digital = (steps ?? []).filter((step) => step.channel !== "LETTER")
  const openDigital = digital.filter((step) => !terminal.has(step.status))
  const openLetters = (steps ?? []).filter(
    (step) => step.channel === "LETTER" && !terminal.has(step.status),
  )
  const proof = presentation.evidence?.kind
  const delivered = proof === "letter_delivered" || proof === "letter_sent"
  const state: WorkOrderAnnouncementOverview["state"] = [
    "CONFIRMED",
    "WHATSAPP_READ",
    "LETTER_DELIVERED",
    "LETTER_SENT",
    "SECURELY_ANNOUNCED",
  ].includes(presentation.definition.value)
    ? "secured"
    : presentation.definition.value === "COMPLETED" || presentation.definition.value === "CANCELED"
      ? "completed"
      : presentation.definition.value === "RESERVED"
        ? "no_plan"
        : presentation.definition.value === "ANNOUNCEMENT_FAILED"
          ? "failed"
          : presentation.definition.value === "LETTER_PENDING" &&
              communicationPresentation?.next_action !== "automatic"
            ? "letter_required"
            : ["ANNOUNCEMENT_UNSECURED", "ANNOUNCEMENT_UNKNOWN", "UNKNOWN", "NOSHOW"].includes(
                  presentation.definition.value,
                )
              ? "unsecured"
              : "running"

  return {
    state,
    label: presentation.label,
    subLabel: presentation.nextAction,
    definition: presentation.definition,
    tooltip: presentation.tooltip,
    signals: [],
    evidence: {
      hasDigitalAnnouncementPath: digital.length > 0,
      hasOpenDigitalStep: openDigital.length > 0,
      hasVerifiedDigitalStep: false,
      verifiedDigitalLabel: null,
      hasOpenLetterStep: openLetters.length > 0,
      hasCompletedLetterStep: delivered,
      completedLetterLabel: delivered
        ? proof === "letter_sent"
          ? "Brief versendet"
          : "Brief zugestellt"
        : null,
      guaranteedManualLetterAction:
        openLetters.length > 0 &&
        digital.length === 0 &&
        (communicationPresentation?.next_action == null ||
          communicationPresentation.next_action === "manual_letter"),
    },
    searchText: [presentation.label, presentation.detail, presentation.nextAction]
      .filter(Boolean)
      .join(" "),
  }
}
